import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorReporting } from '../request-options.ts';

interface UserActionOptions {
  changelog?: string;
  syfGraceAcknowledged?: boolean;
}

export function deactivateUserRequest(
  userId: string,
  options: UserActionOptions = {}
): RequestInfo {
  const { changelog, syfGraceAcknowledged } = options;

  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'users' },
    op: 'query',
    resourcePath: 'users',
  };
  const url = buildBaseURL(urlOptions);
  const body = JSON.stringify({
    data: { attributes: { _changelog: changelog, syf_grace_acknowledged: syfGraceAcknowledged } },
  });
  return {
    url: `${url}/${userId}/deactivate-user`,
    method: 'DELETE',
    body,
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
  };
}

export function reactivateUserRequest(
  userId: string,
  options: UserActionOptions = {}
): RequestInfo {
  const { changelog } = options;

  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'users' },
    op: 'query',
    resourcePath: 'users',
  };
  const url = buildBaseURL(urlOptions);
  const body = JSON.stringify({ data: { attributes: { _changelog: changelog } } });
  return {
    url: `${url}/${userId}/reactivate-user`,
    method: 'PATCH',
    body,
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
  };
}

export function wellnessInfoRequest(userId: string): RequestInfo {
  const url = usersBaseUrl();
  return {
    url: `${url}/${userId}/user-profile?profile_key=UserProfileAttribute.WELLNESS_TRAIN`,
    method: 'GET',
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
    // Non-critical wellness fetch — see SkipErrorReporting docs.
    options: {
      [SkipErrorReporting]: true,
    },
  };
}

function usersBaseUrl() {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'users' },
    op: 'query',
    resourcePath: 'users',
  };

  return buildBaseURL(urlOptions);
}

function wellnessRecalculationBody(userId: string) {
  return JSON.stringify({
    data: {
      type: 'profile-attributes',
      attributes: {},
      relationships: {
        user: {
          data: {
            type: 'users',
            id: userId,
          },
        },
      },
    },
  });
}

function wellnessRecalculationRequest(userId: string, path: string, method: 'PATCH' | 'POST') {
  const url = usersBaseUrl();

  return {
    url: `${url}/${userId}/${path}`,
    method,
    body: wellnessRecalculationBody(userId),
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
    // Non-critical, fire-and-forget recalculation — see SkipErrorReporting docs.
    options: {
      [SkipErrorReporting]: true,
    },
  };
}

export function recalculateWellnessRequest(userId: string): RequestInfo {
  return wellnessRecalculationRequest(userId, 'user-wellness-train', 'PATCH');
}

export function enqueueWellnessRecalculationRequest(userId: string): RequestInfo {
  return wellnessRecalculationRequest(userId, 'queue-user-wellness-train', 'POST');
}

/**
 * GET /users/:id/summarize-loans — aggregate snapshot of the user's
 * loans for PSLF/IDR/repayment-strategy dashboards.
 */
export function getLoanSummaryRequest(userId: string): RequestInfo {
  return {
    url: `${usersBaseUrl()}/${userId}/summarize-loans`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/** GET /users/zendesk-jwt — short-lived JWT for the Zendesk widget. */
export function getZendeskJwtRequest(): RequestInfo {
  return {
    url: `${usersBaseUrl()}/zendesk-jwt`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

export interface UserAggregateQuery {
  /** The model whose data is being aggregated (e.g. 'notification-history'). */
  model: string;
  /** Property used to group the data before aggregation. */
  groupBy?: string;
  /** Field name -> formula (e.g. { value: 'count()' }). */
  rollups?: Record<string, string>;
}

export interface UserAggregateRow {
  /** The value of the `groupBy` property for this group. */
  name?: string;
  [rollup: string]: unknown;
}

export interface UserAggregateResponse {
  data: UserAggregateRow[];
}

/** GET /users/:id/aggregate returns data scoped to what userId can access. */
export function userAggregateRequest(userId: string, query: UserAggregateQuery): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'users' },
    op: 'query',
    resourcePath: 'users',
  };
  const params = new URLSearchParams();
  params.set('model', query.model);
  if (query.groupBy) params.set('groupBy', query.groupBy);
  for (const [field, formula] of Object.entries(query.rollups ?? {})) {
    params.set(`rollups[${field}]`, formula);
  }
  return {
    url: `${buildBaseURL(urlOptions)}/${userId}/aggregate?${params.toString()}`,
    method: 'GET',
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}
